import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type {
  SchoolBasedSelectionCourseForm,
  SchoolBasedSelectionMember,
  SchoolBasedSelectionSlot,
  SchoolBasedSelectionTask,
  SchoolBasedSelectionTaskForm
} from '@/types/school-based-selection'
import { schoolBasedSelectionApi } from '@/api/school-based-selection'
import { registerTenantScopedReset } from '@/stores/_tenantScopedRegistry'

export const useSchoolBasedSelectionStore = defineStore('school-based-selection', () => {
  const tasks = ref<SchoolBasedSelectionTask[]>([])
  const members = ref<SchoolBasedSelectionMember[]>([])
  const currentTaskId = ref<number | null>(null)
  const currentView = ref<'tasks' | 'detail'>('tasks')
  const loading = ref(false)
  const error = ref<Error | null>(null)

  const currentTask = computed(() => tasks.value.find((task) => task.id === currentTaskId.value) ?? null)

  function fail(e: unknown): never {
    error.value = e instanceof Error ? e : new Error(String(e))
    throw error.value
  }

  async function fetchTasks(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      tasks.value = await schoolBasedSelectionApi.listTasks()
    } catch (e) {
      error.value = e instanceof Error ? e : new Error(String(e))
    } finally {
      loading.value = false
    }
  }

  /** 拉取任务最新数据并更新缓存（状态变更后调用）。 */
  async function refreshTask(taskId: number): Promise<SchoolBasedSelectionTask | null> {
    try {
      const task = await schoolBasedSelectionApi.getTask(taskId)
      upsertLocal(task)
      return task
    } catch (e) {
      fail(e)
    }
  }

  function selectTask(taskId: number): void {
    currentTaskId.value = taskId
    currentView.value = 'detail'
    void fetchMembers(taskId)
    void refreshTask(taskId)
  }

  function backToTasks(): void {
    currentView.value = 'tasks'
    currentTaskId.value = null
    members.value = []
    void fetchTasks()
  }

  async function createTask(form: SchoolBasedSelectionTaskForm): Promise<SchoolBasedSelectionTask> {
    const task = await schoolBasedSelectionApi.createTask(form).catch(fail)
    tasks.value.unshift(task)
    return task
  }

  async function updateTask(taskId: number, payload: Partial<Record<string, unknown>>): Promise<SchoolBasedSelectionTask> {
    const task = await schoolBasedSelectionApi.updateTask(taskId, payload).catch(fail)
    upsertLocal(task)
    return task
  }

  async function deleteTask(taskId: number): Promise<void> {
    await schoolBasedSelectionApi.removeTask(taskId).catch(fail)
    const index = tasks.value.findIndex((task) => task.id === taskId)
    if (index !== -1) {
      tasks.value.splice(index, 1)
    }
    if (currentTaskId.value === taskId) {
      backToTasks()
    }
  }

  async function publishTask(taskId: number): Promise<SchoolBasedSelectionTask> {
    return refreshTaskAfter(() => schoolBasedSelectionApi.publishTask(taskId), taskId)
  }

  async function withdrawTask(taskId: number): Promise<SchoolBasedSelectionTask> {
    return refreshTaskAfter(() => schoolBasedSelectionApi.withdrawTask(taskId), taskId)
  }

  async function closeTask(taskId: number): Promise<SchoolBasedSelectionTask> {
    return refreshTaskAfter(() => schoolBasedSelectionApi.closeTask(taskId), taskId)
  }

  async function copyTask(taskId: number): Promise<SchoolBasedSelectionTask> {
    const task = await schoolBasedSelectionApi.copyTask(taskId).catch(fail)
    tasks.value.unshift(task)
    return task
  }

  /** 启用：已截止任务物化名单 + 课位为课时链。 */
  async function activateTask(taskId: number): Promise<void> {
    await schoolBasedSelectionApi.activateTask(taskId).catch(fail)
    await refreshTask(taskId)
  }

  /** 停用：作废课时链，任务回到已截止未启用态。 */
  async function deactivateTask(taskId: number): Promise<SchoolBasedSelectionTask> {
    return refreshTaskAfter(() => schoolBasedSelectionApi.deactivateTask(taskId), taskId)
  }

  // ── 课程与课位（草稿态） ──

  async function addCourse(taskId: number, form: SchoolBasedSelectionCourseForm): Promise<void> {
    await schoolBasedSelectionApi.addCourse(taskId, form).catch(fail)
    await refreshTask(taskId)
  }

  async function updateCourse(taskId: number, courseId: number, form: SchoolBasedSelectionCourseForm): Promise<void> {
    await schoolBasedSelectionApi.updateCourse(taskId, courseId, form).catch(fail)
    await refreshTask(taskId)
  }

  async function removeCourse(taskId: number, courseId: number): Promise<void> {
    await schoolBasedSelectionApi.removeCourse(taskId, courseId).catch(fail)
    await refreshTask(taskId)
  }

  async function saveCourseSlots(taskId: number, courseId: number, slots: SchoolBasedSelectionSlot[]): Promise<void> {
    await schoolBasedSelectionApi.saveCourseSlots(taskId, courseId, slots).catch(fail)
    await refreshTask(taskId)
  }

  // ── 名单 ──

  async function fetchMembers(taskId: number): Promise<void> {
    try {
      members.value = await schoolBasedSelectionApi.listMembers(taskId)
    } catch (e) {
      fail(e)
    }
  }

  async function addMembers(taskId: number, studentIds: number[]): Promise<number> {
    const added = await schoolBasedSelectionApi.addMembers(taskId, studentIds).catch(fail)
    await Promise.all([fetchMembers(taskId), refreshTask(taskId)])
    return added
  }

  async function removeMember(taskId: number, studentId: number): Promise<void> {
    await schoolBasedSelectionApi.removeMember(taskId, studentId).catch(fail)
    await Promise.all([fetchMembers(taskId), refreshTask(taskId)])
  }

  // ── 教师代选/代退/指派 ──

  async function teacherEnroll(taskId: number, studentId: number, taskCourseId: number): Promise<void> {
    await schoolBasedSelectionApi.teacherEnroll(taskId, studentId, taskCourseId).catch(fail)
    await Promise.all([refreshTask(taskId), fetchMembers(taskId)])
  }

  async function teacherDrop(taskId: number, studentId: number, taskCourseId: number): Promise<void> {
    await schoolBasedSelectionApi.teacherDrop(taskId, studentId, taskCourseId).catch(fail)
    await Promise.all([refreshTask(taskId), fetchMembers(taskId)])
  }

  async function assign(taskId: number, studentId: number, taskCourseId: number): Promise<void> {
    await schoolBasedSelectionApi.assign(taskId, studentId, taskCourseId).catch(fail)
    await Promise.all([refreshTask(taskId), fetchMembers(taskId)])
  }

  /** 切换租户 / 登出时清空本 Store，防止跨租户任务数据残留。 */
  function resetAll(): void {
    tasks.value = []
    members.value = []
    currentTaskId.value = null
    currentView.value = 'tasks'
    loading.value = false
    error.value = null
  }

  // ── 内部 ──

  function upsertLocal(task: SchoolBasedSelectionTask): void {
    const index = tasks.value.findIndex((item) => item.id === task.id)
    if (index !== -1) {
      tasks.value[index] = task
    } else {
      tasks.value.unshift(task)
    }
  }

  async function refreshTaskAfter(
    action: () => Promise<SchoolBasedSelectionTask>,
    taskId: number
  ): Promise<SchoolBasedSelectionTask> {
    await action().catch(fail)
    const task = await refreshTask(taskId)
    return task ?? (currentTask.value as SchoolBasedSelectionTask)
  }

  return {
    resetAll,
    tasks,
    members,
    currentTaskId,
    currentTask,
    currentView,
    loading,
    error,
    fetchTasks,
    refreshTask,
    selectTask,
    backToTasks,
    createTask,
    updateTask,
    deleteTask,
    publishTask,
    withdrawTask,
    closeTask,
    copyTask,
    activateTask,
    deactivateTask,
    addCourse,
    updateCourse,
    removeCourse,
    saveCourseSlots,
    fetchMembers,
    addMembers,
    removeMember,
    teacherEnroll,
    teacherDrop,
    assign
  }
})

registerTenantScopedReset('school-based-selection', () => useSchoolBasedSelectionStore().resetAll())
